import { tz } from 'moment';
import moment from 'moment';
import { formatDate } from '../../shared/helpers';

export interface CompetitionIntlValues {
  endHour: string;
  endDate: string;
  startHour: string;
  link?: string;
  linkComponent?: (text: string) => JSX.Element;
  signUpEndHour: string;
  signUpEndDate: string;
  startDate: string;
  nextStage?: string;
  nextStageStartDate?: string;
}

/**
 * Formats the given dates associated with a competition and generates a hash with formatted date values needed for
 * messages shown to the user.
 * start, end and signUpEnd are dates of the from 2019-11-17T23:59:59Z
 */
export function extractDateComponents(start: string, end: string, signUpEnd: string): CompetitionIntlValues {
  const startDate = moment(start, 'YYYY-M-DDTHH:mm:ssZ', true);
  const endDate = moment(end, 'YYYY-M-DDTHH:mm:ssZ', true);
  const signUpEndDate = moment(signUpEnd, 'YYYY-M-DDTHH:mm:ssZ', true);

  return {
    startDate: formatDate(startDate),
    startHour: startDate.tz(tz.guess()).format('LTS z'),
    endDate: formatDate(endDate),
    endHour: endDate.tz(tz.guess()).format('LTS z'),
    signUpEndDate: formatDate(signUpEndDate),
    signUpEndHour: signUpEndDate.tz(tz.guess()).format('LTS z'),
    nextStage: undefined,
    nextStageStartDate: undefined,
    link: undefined,
  };
}

export const alphathonLinkPrefix = 'GA2022S';
export const iitkAlphathonPrefix = 'iitkalphathon2023';
export const mapc2023Prefix = 'MAPC2023';
export const iqc2023Prefix = 'IQC2023';

interface Stage {
  id: string;
  name: string;
  type: 'competition' | 'leaderboard';
  isReady: boolean;
  startDate?: string;
  leaderboardUrl?: string;
}

function getNextStage(currentStage: string): Stage | undefined {
  const stages: Stage[] = [
    { id: '1', name: '1', type: 'competition', isReady: true },
    { id: '2', name: '2', type: 'competition', isReady: true },
    { id: '2F', name: '2 (Final)', type: 'leaderboard', isReady: true },
    { id: '3', name: '3', type: 'competition', isReady: true },
    { id: '3F', name: '3 (Final)', type: 'leaderboard', isReady: true, leaderboardUrl: '/messages/announcements' },
  ];

  const stageIndex = stages.findIndex(stage => stage.id === currentStage);
  if (stageIndex < 0 || stageIndex === stages.length - 1) {
    return;
  }

  return stages[stageIndex + 1];
}

export function getNextStageProps(id: string) {
  if (id.startsWith(alphathonLinkPrefix)) {
    const currentStage = id.substring(alphathonLinkPrefix.length);
    const nextStage = getNextStage(currentStage);
    if (nextStage) {
      return {
        nextStage: nextStage.name,
        nextStageStartDate: nextStage.startDate,
        nextStageType: nextStage.type,
        isNextStageReady: nextStage.isReady,
        isNextStageExternalLeaderboard: !!nextStage.leaderboardUrl,
        link: nextStage.leaderboardUrl ?? `/competition/${alphathonLinkPrefix}${nextStage.id}`,
      };
    }
  }

  return null;
}

interface CheckIfUserEigibleProps {
  competition: any;
  currentCountry: string;
  currentUniversity: string;
  countriesOptions: any;
}

export const checkIfUserIsEligibleToCompetition = ({
  competition,
  currentCountry,
  currentUniversity,
  countriesOptions,
}: CheckIfUserEigibleProps) => {
  if (competition && (currentCountry !== '' || currentUniversity !== '')) {
    const countries = competition.get('countries');
    const excludedCountries = competition.get('excludedCountries');
    const universities = competition.get('universities');

    // Check If the university is invalid and if yes get all universities valid
    const checkIfUniversityIsValid =
      !universities || currentUniversity === '' || (universities && universities.includes(currentUniversity));
    const errorMessageUniversities = !checkIfUniversityIsValid && universities ? universities.join(', ') : '';
    // Check If Country is invalid and if yes get all countries valid
    const checkIfCountryIsValid = !countries || currentCountry === '' || (countries && countries.includes(currentCountry));
    const countriesLabels =
      !checkIfCountryIsValid &&
      countriesOptions &&
      countries &&
      countries.map((item: string) => (countriesOptions.get(item) ? countriesOptions.get(item) : item));
    const errorMessageCountries = !checkIfCountryIsValid && countriesLabels ? countriesLabels.join(', ') : '';

    // Check If Country is excluded and if yes get all countries excluded
    const checkIfCountryIsExcluded = excludedCountries && currentCountry !== '' && excludedCountries.includes(currentCountry);
    const excludedcountriesLabels =
      checkIfCountryIsExcluded &&
      excludedCountries &&
      countriesOptions &&
      excludedCountries.map((item: string) => (countriesOptions.get(item) ? countriesOptions.get(item) : item));
    const errorMessageCountriesExcluded = checkIfCountryIsExcluded && excludedcountriesLabels ? excludedcountriesLabels.join(', ') : '';

    const values = {
      onlyExcludedCountry: Boolean(checkIfCountryIsExcluded && checkIfCountryIsValid && checkIfUniversityIsValid),
      invalidUniversityAndCountry: Boolean(!checkIfUniversityIsValid && !checkIfCountryIsValid),
      excludedCountryPlusOneMoreInvalid: Boolean(
        (checkIfCountryIsExcluded && !checkIfCountryIsValid) || (checkIfCountryIsExcluded && !checkIfUniversityIsValid)
      ),
      errorMessageUniversities,
      errorMessageCountries,
      errorMessageCountriesExcluded,
      competitionName: competition.get('name'),
    };
    if (!checkIfUniversityIsValid || !checkIfCountryIsValid || checkIfCountryIsExcluded) {
      return [{ id: 'invalid.competition.agreement', defaultMessage: 'invalid.competition.agreement', values }];
    } else {
      return [];
    }
  } else {
    return [];
  }
};
